/**
 * 딜 상태값 badge 컬러 — 단일 소스.
 *
 * 진행 단계에 따라 "차갑고 중립적인 색 → 주목 색 → 성공 색" 으로 이어짐.
 *   리드 접수됨(blue) → 리드 검토됨(indigo) → 소싱 확인중(amber)
 *   → 견적 작성 완료(green) → 견적 발송 완료(teal) → 수주(진한 보라 채움 — 최종 성사 강조)
 *
 * 수주 이후 5개(매입 진행중~취소)는 서안드레 스펙 밖 — 같은 톤으로 확장.
 * 색만 바꾸려면 아래 값만 수정하면 전 화면(리스트/드로어/상세)에 반영됨.
 *
 * 각 badge = 연한 background + 진한 text + 같은 계열 연한 border (가독성).
 */
import type { DealStatus } from '../api/client';

export interface StatusColor {
  bg:     string;
  text:   string;
  border: string;
}

export const DEAL_STATUS_COLORS: Record<DealStatus, StatusColor> = {
  // ── 서안드레 스펙 (6) ──────────────────────────────────────────────
  '리드 접수됨':   { bg: '#E0F2FE', text: '#0369A1', border: '#BAE6FD' }, // sky(하늘) — 접수
  '리드 검토됨':   { bg: '#E0E7FF', text: '#3730A3', border: '#C7D2FE' }, // indigo(남보라, 진하게) — 검토
  '소싱 확인중':   { bg: '#FEF3C7', text: '#B45309', border: '#FDE68A' }, // amber
  '견적 작성 완료': { bg: '#ECFCCB', text: '#4D7C0F', border: '#D9F99D' }, // lime(연두) — 작성됨
  '견적 발송 완료': { bg: '#99F6E4', text: '#0F766E', border: '#5EEAD4' }, // teal(진하게) — 발송됨
  '수주':          { bg: '#6D28D9', text: '#FFFFFF', border: '#5B21B6' }, // 진한 보라(채움) — 최종 성사 강조(유일한 solid 뱃지)
  // ── 수주 이후 확장 (5) — 나중에 자유 조정 ─────────────────────────
  '매입 진행중':   { bg: '#CFFAFE', text: '#0E7490', border: '#A5F3FC' }, // cyan
  '세팅중':        { bg: '#EDE9FE', text: '#6D28D9', border: '#DDD6FE' }, // violet
  '세팅 완료':     { bg: '#F3E8FF', text: '#7E22CE', border: '#E9D5FF' }, // purple
  '납품 완료':     { bg: '#F1F5F9', text: '#334155', border: '#E2E8F0' }, // slate(완료)
  '취소':          { bg: '#F3F4F6', text: '#6B7280', border: '#E5E7EB' }, // gray(중립)
};

const FALLBACK: StatusColor = { bg: '#F3F4F6', text: '#6B7280', border: '#E5E7EB' };

/** 상태 badge 인라인 스타일 문자열. CSS 카스케이드 충돌 회피 위해 인라인 적용. */
export function statusBadgeStyle(status: DealStatus): string {
  const c = DEAL_STATUS_COLORS[status] ?? FALLBACK;
  return `background:${c.bg};color:${c.text};border:1px solid ${c.border};`;
}
